import { apiClient } from './client'
import type {
  Instrument,
  Position,
  Signal,
  Strategy,
  BacktestResult,
  Candle,
  DownloadJob,
  QuotaStatus,
  DownloadRequest,
  DownloadEstimate,
  InstrumentList,
  InstrumentListCreate,
  InstrumentListUpdate,
  InstrumentListMembership,
  InstrumentListMembershipCreate,
  PortfolioBacktestRequest,
  PortfolioBacktestSummary,
  MarginRefreshResponse,
  IGCredentialSet,
  IGCredentialSetCreate,
  IGCredentialSetUpdate,
  GridSearchRequest,
  SensitivityAnalysisRequest,
  BayesianOptimizationRequest,
  WalkForwardRequest,
  OptimizationRun,
  OptimizationRunDetail,
  ValidationRun,
} from '../types'

export * from './auth'
export { apiClient }

// Instruments
export const instrumentsApi = {
  getAll: async (): Promise<Instrument[]> => {
    const response = await apiClient.get('/instruments/')
    return response.data
  },

  getById: async (id: number): Promise<Instrument> => {
    const response = await apiClient.get(`/instruments/${id}`)
    return response.data
  },

  search: async (query: string): Promise<any[]> => {
    const response = await apiClient.get('/instruments/search', { params: { query } })
    return response.data
  },

  add: async (epic: string): Promise<Instrument> => {
    const response = await apiClient.post(`/instruments/add/${epic}`)
    return response.data
  },

  setAlphaVantageSymbol: async (instrumentId: number, symbol: string): Promise<{ message: string; instrument_id: number; epic: string; alpha_vantage_symbol: string | null }> => {
    const response = await apiClient.patch(`/instruments/${instrumentId}/alpha-vantage-symbol`, null, {
      params: { symbol }
    })
    return response.data
  },

  refreshMargin: async (instrumentId: number): Promise<MarginRefreshResponse> => {
    const response = await apiClient.post(`/instruments/${instrumentId}/refresh-margin`)
    return response.data
  },
}

// Positions
export const positionsApi = {
  getAll: async (status?: 'OPEN' | 'CLOSED'): Promise<Position[]> => {
    const response = await apiClient.get('/positions/', { params: { status } })
    return response.data
  },

  getById: async (id: number): Promise<Position> => {
    const response = await apiClient.get(`/positions/${id}`)
    return response.data
  },
}

// Working Orders
export interface StrategyConfig {
  id: number
  strategy_id: number
  instrument_id: number | null
  timeframe: string | null
  strategy?: {
    id: number
    name: string
    description?: string
  }
}

export interface WorkingOrder {
  id: number
  user_id: number
  instrument_id: number
  strategy_config_id: number | null
  position_id: number | null
  ig_deal_id: string | null
  order_type: string
  order_purpose: string
  status: string
  order_level: number
  size: number
  stop_distance: number | null
  limit_distance: number | null
  strategy_state: any | null
  paired_order_id: number | null
  created_at: string
  updated_at: string
  last_synced_at: string | null
  executed_at: string | null
  cancelled_at: string | null
  instrument?: Instrument
  strategy_config?: StrategyConfig
}

export const workingOrdersApi = {
  getPending: async (): Promise<WorkingOrder[]> => {
    const response = await apiClient.get('/working-orders/pending')
    return response.data
  },

  getActive: async (): Promise<WorkingOrder[]> => {
    const response = await apiClient.get('/working-orders/active')
    return response.data
  },

  getHistory: async (): Promise<WorkingOrder[]> => {
    const response = await apiClient.get('/working-orders/history')
    return response.data
  },

  getById: async (id: number): Promise<WorkingOrder> => {
    const response = await apiClient.get(`/working-orders/${id}`)
    return response.data
  },

  approve: async (id: number): Promise<{ message: string; orders: WorkingOrder[] }> => {
    const response = await apiClient.post(`/working-orders/${id}/approve`)
    return response.data
  },

  cancel: async (id: number): Promise<{ message: string; orders: WorkingOrder[] }> => {
    const response = await apiClient.post(`/working-orders/${id}/cancel`)
    return response.data
  },
}

// Strategies
export const strategiesApi = {
  getAll: async (): Promise<Strategy[]> => {
    const response = await apiClient.get('/strategies/')
    return response.data
  },

  getById: async (id: number): Promise<Strategy> => {
    const response = await apiClient.get(`/strategies/${id}`)
    return response.data
  },
}

// Backtests
export const backtestsApi = {
  run: async (data: {
    strategy_id: number
    instrument_id: number
    start_date: string
    end_date: string
    initial_capital: number
    timeframe?: string
    strategy_parameters?: string
    use_sample_data?: boolean
    use_margin_validation?: boolean
    margin_buffer_percent?: number
    max_positions_per_instrument?: number
    filter_halt_weekdays?: number[] | null
    filter_halt_time_start?: string | null
    filter_halt_time_end?: string | null
    filter_halt_time_timezone?: string | null
    filter_hide_weekdays?: number[] | null
    position_sizing_method?: string
    position_size_fixed?: number
    risk_percent?: number
    risk_amount_fixed?: number
    max_position_size?: number
  }, background: boolean = true): Promise<any> => {
    const response = await apiClient.post('/backtests/run', data, {
      params: { background }
    })
    return response.data
  },

  getTaskStatus: async (taskId: string): Promise<any> => {
    const response = await apiClient.get(`/backtests/task/${taskId}`)
    return response.data
  },

  getAll: async (): Promise<BacktestResult[]> => {
    const response = await apiClient.get('/backtests/')
    return response.data
  },

  getById: async (id: number): Promise<BacktestResult> => {
    const response = await apiClient.get(`/backtests/${id}`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/backtests/${id}`)
  },
}

// Candles
export const candlesApi = {
  getByInstrument: async (
    instrumentId: number,
    timeframe: string,
    startDate: string,
    endDate: string,
    onlyComplete: boolean = true
  ): Promise<{ candles: Candle[]; total_count: number }> => {
    const response = await apiClient.get(`/candles/instruments/${instrumentId}`, {
      params: { timeframe, start_date: startDate, end_date: endDate, only_complete: onlyComplete },
    })
    return response.data
  },

  getLatest: async (instrumentId: number, timeframe: string): Promise<Candle> => {
    const response = await apiClient.get(`/candles/instruments/${instrumentId}/latest`, {
      params: { timeframe },
    })
    return response.data
  },
}

// Downloads
export const downloadsApi = {
  start: async (data: DownloadRequest): Promise<DownloadJob> => {
    const response = await apiClient.post('/downloads/start', data)
    return response.data
  },

  startAlphaVantage: async (data: DownloadRequest): Promise<DownloadJob> => {
    const response = await apiClient.post('/downloads/start-alpha-vantage', data)
    return response.data
  },

  getAll: async (status?: string): Promise<DownloadJob[]> => {
    const response = await apiClient.get('/downloads/jobs', { params: { status } })
    return response.data
  },

  getById: async (id: number): Promise<DownloadJob> => {
    const response = await apiClient.get(`/downloads/jobs/${id}`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/downloads/jobs/${id}`)
  },

  getQuota: async (): Promise<QuotaStatus> => {
    const response = await apiClient.get('/downloads/quota')
    return response.data
  },

  estimate: async (data: DownloadRequest): Promise<DownloadEstimate> => {
    const response = await apiClient.post('/downloads/estimate', data)
    return response.data
  },

  searchAlphaVantageSymbols: async (query: string): Promise<{ results: any[], query: string }> => {
    const response = await apiClient.get('/downloads/alpha-vantage/search', {
      params: { query }
    })
    return response.data
  },
}

// IG Accounts
export interface IGAccount {
  id: number
  account_id: string
  account_name: string
  account_type: string
  is_preferred: boolean
  balance: number | null
  currency: string | null
  available_balance: number | null
  profit_loss: number | null
  deposit: number | null
  last_synced_at: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export const igAccountsApi = {
  sync: async (): Promise<{ message: string; accounts: IGAccount[] }> => {
    const response = await apiClient.post('/ig-accounts/sync')
    return response.data
  },

  getAll: async (): Promise<{ accounts: IGAccount[] }> => {
    const response = await apiClient.get('/ig-accounts/')
    return response.data
  },

  getById: async (id: number): Promise<IGAccount> => {
    const response = await apiClient.get(`/ig-accounts/${id}`)
    return response.data
  },

  deactivate: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/ig-accounts/${id}`)
    return response.data
  },
}

// Instrument Lists
export const instrumentListsApi = {
  getAll: async (): Promise<InstrumentList[]> => {
    const response = await apiClient.get('/instrument-lists/')
    return response.data
  },

  getById: async (id: number): Promise<InstrumentList> => {
    const response = await apiClient.get(`/instrument-lists/${id}`)
    return response.data
  },

  create: async (data: InstrumentListCreate): Promise<InstrumentList> => {
    const response = await apiClient.post('/instrument-lists/', data)
    return response.data
  },

  update: async (id: number, data: InstrumentListUpdate): Promise<InstrumentList> => {
    const response = await apiClient.patch(`/instrument-lists/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<{ message: string; list_id: number; name: string }> => {
    const response = await apiClient.delete(`/instrument-lists/${id}`)
    return response.data
  },

  getInstruments: async (listId: number): Promise<InstrumentListMembership[]> => {
    const response = await apiClient.get(`/instrument-lists/${listId}/instruments`)
    return response.data
  },

  addInstrument: async (listId: number, data: InstrumentListMembershipCreate): Promise<InstrumentListMembership> => {
    const response = await apiClient.post(`/instrument-lists/${listId}/instruments`, data)
    return response.data
  },

  removeInstrument: async (listId: number, instrumentId: number): Promise<{ message: string; list_id: number; instrument_id: number }> => {
    const response = await apiClient.delete(`/instrument-lists/${listId}/instruments/${instrumentId}`)
    return response.data
  },

  reorderInstrument: async (listId: number, instrumentId: number, newOrderIndex: number): Promise<{ message: string; list_id: number; instrument_id: number; new_order_index: number }> => {
    const response = await apiClient.patch(`/instrument-lists/${listId}/instruments/${instrumentId}/reorder`, null, {
      params: { new_order_index: newOrderIndex }
    })
    return response.data
  },

  enableStreaming: async (listId: number, enabled: boolean): Promise<{ message: string; list_id: number; auto_stream: boolean; instruments_updated: number }> => {
    const response = await apiClient.post(`/instrument-lists/${listId}/enable-streaming`, { enabled })
    return response.data
  },

  enableEOD: async (listId: number, enabled: boolean): Promise<{ message: string; list_id: number; auto_eod_download: boolean; instruments_updated: number }> => {
    const response = await apiClient.post(`/instrument-lists/${listId}/enable-eod`, { enabled })
    return response.data
  },

  getSubscriptionStats: async (listId: number): Promise<{ list_id: number; list_name: string; auto_stream: boolean; auto_eod_download: boolean; total_instruments: number; streaming_count: number; eod_download_count: number }> => {
    const response = await apiClient.get(`/instrument-lists/${listId}/subscription-stats`)
    return response.data
  },
}

// Portfolio Backtesting
export const portfolioBacktestsApi = {
  run: async (data: PortfolioBacktestRequest): Promise<PortfolioBacktestSummary> => {
    const response = await apiClient.post('/backtests/portfolio/run', data)
    return response.data
  },

  getAll: async (): Promise<PortfolioBacktestSummary[]> => {
    const response = await apiClient.get('/backtests/portfolio/')
    return response.data
  },

  getById: async (id: number): Promise<PortfolioBacktestSummary> => {
    const response = await apiClient.get(`/backtests/portfolio/${id}`)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/backtests/portfolio/${id}`)
  },

  getOriginalParams: async (id: number) => {
    const response = await apiClient.get(`/backtests/portfolio/${id}/original-params`)
    return response.data
  },
}

// Subscriptions
export interface SubscriptionStatus {
  instrument_id: number
  stream_enabled: boolean
  eod_download_enabled: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface SubscriptionSummary {
  total_instruments: number
  streaming_count: number
  eod_download_count: number
  active_streams: number
}

export const subscriptionsApi = {
  getStatus: async (instrumentId: number): Promise<SubscriptionStatus> => {
    const response = await apiClient.get(`/subscriptions/status/${instrumentId}`)
    return response.data
  },

  toggleStreaming: async (instrumentId: number, enabled: boolean): Promise<SubscriptionStatus> => {
    const response = await apiClient.post(`/subscriptions/stream/${instrumentId}`, { enabled })
    return response.data
  },

  toggleEOD: async (instrumentId: number, enabled: boolean): Promise<SubscriptionStatus> => {
    const response = await apiClient.post(`/subscriptions/eod/${instrumentId}`, { enabled })
    return response.data
  },

  getSummary: async (): Promise<SubscriptionSummary> => {
    const response = await apiClient.get('/subscriptions/summary')
    return response.data
  },

  getAll: async (): Promise<SubscriptionStatus[]> => {
    const response = await apiClient.get('/subscriptions/')
    return response.data
  },

  delete: async (instrumentId: number): Promise<{ message: string; instrument_id: number }> => {
    const response = await apiClient.delete(`/subscriptions/${instrumentId}`)
    return response.data
  },
}

// Timezones
export interface Timezone {
  id: number
  name: string
  iana_timezone: string
  description: string | null
  is_active: boolean
  created_at: string
}

export const timezonesApi = {
  getAll: async (): Promise<Timezone[]> => {
    const response = await apiClient.get('/timezones/')
    return response.data
  },

  getById: async (id: number): Promise<Timezone> => {
    const response = await apiClient.get(`/timezones/${id}`)
    return response.data
  },
}

// IG Credential Sets
export const credentialSetsApi = {
  getAll: async (): Promise<IGCredentialSet[]> => {
    const response = await apiClient.get('/ig-credentials/')
    return response.data
  },

  getById: async (id: number): Promise<IGCredentialSet> => {
    const response = await apiClient.get(`/ig-credentials/${id}`)
    return response.data
  },

  create: async (data: IGCredentialSetCreate): Promise<IGCredentialSet> => {
    const response = await apiClient.post('/ig-credentials/', data)
    return response.data
  },

  update: async (id: number, data: IGCredentialSetUpdate): Promise<IGCredentialSet> => {
    const response = await apiClient.patch(`/ig-credentials/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/ig-credentials/${id}`)
    return response.data
  },

  setPreferred: async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/ig-credentials/${id}/set-preferred`)
    return response.data
  },

  getPreferred: async (): Promise<IGCredentialSet | null> => {
    try {
      const response = await apiClient.get('/ig-credentials/preferred/current')
      return response.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },
}

// Optimization
export const optimizationApi = {
  startGridSearch: async (request: GridSearchRequest): Promise<OptimizationRun> => {
    const response = await apiClient.post('/optimization/grid-search', request)
    return response.data
  },

  startSensitivityAnalysis: async (request: SensitivityAnalysisRequest): Promise<OptimizationRun> => {
    const response = await apiClient.post('/optimization/sensitivity', request)
    return response.data
  },

  listRuns: async (params?: { method?: string; status?: string; limit?: number }): Promise<(OptimizationRun | ValidationRun)[]> => {
    const response = await apiClient.get('/optimization/runs', { params })
    return response.data
  },

  getRun: async (runId: number, includeResults: boolean = true): Promise<OptimizationRunDetail | ValidationRun> => {
    const response = await apiClient.get(`/optimization/${runId}`, {
      params: { include_results: includeResults }
    })
    return response.data
  },

  deleteRun: async (runId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/optimization/${runId}`)
    return response.data
  },

  cancelRun: async (runId: number): Promise<{ message: string }> => {
    const response = await apiClient.post(`/optimization/${runId}/cancel`)
    return response.data
  },

  // Walk-Forward Analysis
  startWalkForward: async (request: WalkForwardRequest): Promise<OptimizationRun | ValidationRun> => {
    const response = await apiClient.post('/optimization/walk-forward', request)
    return response.data
  },

  getWalkForwardWindows: async (runId: number): Promise<any[]> => {
    const response = await apiClient.get(`/optimization/${runId}/walk-forward-windows`)
    return response.data
  },

  getValidationWindows: async (validationRunId: number): Promise<any[]> => {
    const response = await apiClient.get(`/optimization/validations/${validationRunId}/windows`)
    return response.data
  },

  // Monte Carlo Validation
  startMonteCarlo: async (request: { optimization_run_id: number; bootstrap_iterations?: number; perturbation_iterations?: number; perturbation_pct?: number }): Promise<ValidationRun> => {
    const response = await apiClient.post('/optimization/monte-carlo', request)
    return response.data
  },

  // Bayesian Optimization
  startBayesianOptimization: async (request: BayesianOptimizationRequest): Promise<OptimizationRun> => {
    const response = await apiClient.post('/optimization/bayesian', request)
    return response.data
  },

  // Optimization Backtests
  getOptimizationBacktests: async (runId: number): Promise<any[]> => {
    const response = await apiClient.get(`/optimization/${runId}/backtests`)
    return response.data
  },

  getOptimizationBacktestDetails: async (runId: number, backtestId: number): Promise<any> => {
    const response = await apiClient.get(`/optimization/${runId}/backtests/${backtestId}`)
    return response.data
  },

  // Pareto Frontier Analysis
  getParetoAnalysis: async (runId: number, objectives: Array<{ metric_name: string; direction: string }>): Promise<any> => {
    const response = await apiClient.post(`/optimization/${runId}/pareto-analysis`, { objectives })
    return response.data
  },
}
